/* eslint-disable no-unused-vars */
import { Grid, Pagination } from '@achieve/ascend'
import Section from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import { get as _get } from 'lodash-es'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink'
import styles from './PaginatedArticlesList.module.scss'
import { Eyebrow } from 'components/Eyebrow'
import { useContext, useEffect, useState } from 'react'
import { getBaseURL } from 'utils/shared/filter'
import { useRouter } from 'next/router'
import AppStoreButtons from 'components/AppStoreButtons/AppStoreButtons'
import { ArticlesFilterContext } from 'providers/ArticlesFilterProvider'
import { getSlug, normalizeCategoryName } from 'utils/conversions'
import { Image } from 'components/Image'
import { PAGE_LIMIT_ARTICLES_DEFAULT } from 'services/contentful/constants'
import dynamic from 'next/dynamic'
const AchieveSkeleton = dynamic(() => import('components/AchieveSkeleton/AchieveSkeleton'))
import { mainProductConfig } from 'services/contentful/helpers'
import { LayoutContext } from 'providers/LayoutProvider'

function PaginatedArticlesList({ content }) {
  const router = useRouter()
  const categoryname = router.query.categoryname
  let { cardCta, listArticles } = useMemoizedContentGetter(content, ['cardCta', 'listArticles'])
  const config = _get(content, 'fields.additionalConfiguration.jsonContent', '')
  const [initialRender, setInitialRender] = useState(true)
  const { state, dispatch } = useContext(ArticlesFilterContext)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (initialRender) {
      if (router.query.search) {
        fetchArticlesSearch({ dispatch })
      }
    }
    dispatch({ type: 'SET_PAGE', payload: router.query ? router.query.page : 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getBody = () => {
    let body = {
      content_type: listArticles?.contentType,
      pageLimit: config.pageLimit ?? PAGE_LIMIT_ARTICLES_DEFAULT,
      page: state?.page ?? router.query.page ?? 1,
      filterFields: {
        contentType: listArticles?.contentTypeArticle,
        showContent: true,
      },
      orderBy: state?.orderBy,
    }

    if (categoryname && categoryname !== '*' && !listArticles?.isSubcategory) {
      body.category = listArticles?.categoryName
    }

    if (state?.keywordsFilter !== '') {
      body.keywords = state?.keywordsFilter
    }

    if (listArticles?.allPage) {
      delete body.category
    }

    if (listArticles?.isSubcategory) {
      body.category = null
      body.subcategory = listArticles?.categoryName
    }

    return body
  }

  const fetchArticlesSearch = async ({ dispatch }) => {
    if (loading) {
      return
    }
    try {
      setLoading(true)
      const body = getBody()
      if (body.keywords) {
        router.query.search = encodeURI(body.keywords)
        router.push({ query: router.query }, null, { shallow: true })
      } else {
        if (router.query.search) {
          dispatch({ type: 'SET_KEYWORDS', payload: router.query.search })
          body.keywords = router.query.search
        }
      }

      const res = await fetch('/api/contentful/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const contentfulJson = await res.json()
      dispatch({
        type: 'SET_ARTICLES',
        payload: { articles: contentfulJson?.items, total: contentfulJson?.total },
      })
      setLoading(false)
    } catch (error) {
      dispatch({ type: 'SET_ARTICLES', payload: { articles: [] } })
      setLoading(false)
    }
  }

  const fetchArticles = async ({ dispatch }) => {
    if (loading) {
      return
    }
    try {
      setLoading(true)
      const body = getBody()
      const res = await fetch('/api/contentful/fetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const contentfulJson = await res.json()
      dispatch({
        type: 'SET_ARTICLES',
        payload: { articles: contentfulJson?.items, total: contentfulJson?.total },
      })
      setLoading(false)
    } catch (error) {
      dispatch({ type: 'SET_ARTICLES', payload: { articles: [] } })
      setLoading(false)
    }
  }

  useEffect(() => {
    //Prevents refetching on initialize
    if (Object.keys(state).length === 0) {
      return
    }

    if (initialRender) {
      setInitialRender(false)
    } else {
      if (router.query.search) {
        fetchArticlesSearch({ dispatch })
      } else {
        fetchArticles({ dispatch })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.page, router.query.search, categoryname, state?.orderBy, state?.keywordsFilter])

  const RenderCard = ({
    idx,
    title,
    primaryCategory,
    hook,
    slug,
    mainImage,
    variant = 'default',
    ctaSection,
    isVideo,
  }) => {
    const isCtaVariant = variant == 'cta'
    const CardContent = (
      <div className={styles['card-container']}>
        <Grid container columnSpacing={2} className={styles['card']}>
          <Grid item xs={isCtaVariant ? 12 : 4} md={isCtaVariant ? 12 : 3} lg={2}>
            <div className={styles['image-container']}>
              {mainImage && (
                <MediaImageStandard
                  alt="render-card-image"
                  content={mainImage}
                  priority={true}
                  aspectRatio={16 / 9}
                />
              )}
              {isVideo && (
                <div className={styles['play-button']}>
                  <Image alt={'Play Button'} height={64} src="/play-btn.svg" width={64} />
                </div>
              )}
            </div>
          </Grid>
          <Grid
            item
            xs={isCtaVariant ? 12 : 8}
            md={isCtaVariant ? 12 : 9}
            lg={10}
            display={'flex'}
            flexDirection={'column'}
            gap={1}
          >
            <Eyebrow
              className={styles['card-eyebrow']}
              content={primaryCategory}
              variant={'labelSm'}
            />
            <Typography
              content={title}
              variant={'labelSm'}
              className={styles['card-title']}
              data-testid={`card-title`}
            />
            <Typography content={hook} variant="labelXs" className={styles['card-hook']} />
            {ctaSection}
          </Grid>
        </Grid>
      </div>
    )
    return (
      <Grid item xs={12} data-variant={variant}>
        {isCtaVariant ? (
          <>{CardContent}</>
        ) : (
          <AchieveLink
            track={{
              list_name: 'Card Article',
              click_position: idx,
              click_id: title,
              click_text: title,
              nav_link_section: 'Content Grid',
              track_event: 'page_nav_click',
              event_action: 'link_click',
              event_type: 'link_click',
            }}
            className={styles['anchor-links']}
            href={`/${getBaseURL()}/${getSlug(primaryCategory)}/${slug}`}
            data-testid="article-list-card-link"
          >
            {CardContent}
          </AchieveLink>
        )}
      </Grid>
    )
  }

  const handleChangePage = (event, page) => {
    dispatch({ type: 'SET_PAGE', payload: page })
    router.push({ query: { ...router.query, page } }, null, { shallow: true, scroll: false })
  }
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const changeOrderSearch = (order) => {
    dispatch({ type: 'SET_ORDER_BY', payload: order })
  }

  const deleteDoubleQuotes = (filterString) => {
    return filterString.split('"').join('')
  }

  const [title, setTitle] = useState()

  useEffect(() => {
    setTitle(
      router.pathname.indexOf('/learn/search') !== -1
        ? state?.keywordsFilter
          ? `Search results for "${deleteDoubleQuotes(state?.keywordsFilter)}"`
          : `All Articles`
        : `All ${
            categoryname != 'all' ? normalizeCategoryName(categoryname) : 'Financial Education'
          } Articles`
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryname, state?.keywordsFilter, router.query.search])

  return (
    <Section id="article_list" data-testid="articles-list-section">
      <Grid container marginBottom={4} marginTop={4} rowGap={2}>
        <Grid item xs={12}>
          <Grid container rowGap={2}>
            <Grid item xs={12} lg={8}>
              <Typography content={title} variant="headingMd" data-testid="article-list-title" />
            </Grid>
            <Grid
              item
              xs={12}
              lg={4}
              display={'flex'}
              flexDirection={'row'}
              columnGap={2}
              justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            >
              <AchieveLink
                noLink
                className={styles['link-filter']}
                data-active={state?.orderBy === 'popular'}
                onClick={() => changeOrderSearch('popular')}
                data-testid="article-list-popular-filter-link"
              >
                <Typography
                  content={categoryname == 'search' ? 'Most Relevant' : 'Most Popular'}
                  variant="labelXs"
                  data-testid="article-list-popular"
                />
              </AchieveLink>
              <AchieveLink
                noLink
                className={styles['link-filter']}
                data-active={state?.orderBy === 'recent'}
                onClick={() => changeOrderSearch('recent')}
                data-testid="article-list-popular-recent-link"
              >
                <Typography
                  content="Most Recent"
                  variant="labelXs"
                  data-testid="article-list-recent"
                />
              </AchieveLink>
            </Grid>
          </Grid>
        </Grid>
        {state?.articlesObject?.articles && state?.articlesObject?.articles.length > 0 && !loading && (
          <Grid item xs={12}>
            <Grid container data-testid="articles-list-cards-container">
              {state?.articlesObject?.articles.map((article, idx) => {
                if (!article) {
                  return null
                }
                const itemsRender = [
                  <RenderCard
                    idx={idx}
                    slug={article.slug}
                    key={`RenderCard-${idx}`}
                    title={article.title}
                    hook={article.hook}
                    primaryCategory={article.primaryCategory}
                    mainImage={article?.mainImage?.mediaContent ?? article?.mainImage?.thumbnail}
                    isVideo={article?.mainImage?.__typename === 'VideoPlayer'}
                  />,
                ]
                if (config?.cardCtaPosition) {
                  if (idx + 1 === config.cardCtaPosition)
                    itemsRender.push(
                      <RenderCard
                        key={`RenderCardCTA-${idx}`}
                        title={cardCta.cardTitleText}
                        hook={cardCta.cardText}
                        primaryCategory={cardCta.eyebrow}
                        mainImage={cardCta?.cardImage}
                        variant="cta"
                        ctaSection={
                          <AppStoreButtons
                            appleButton={cardCta.linkApple}
                            googleButton={cardCta.linkAndroid}
                            appleImage={cardCta.imageApple}
                            googleImage={cardCta.imageAndroid}
                            navLinkSection="Article"
                          />
                        }
                      />
                    )
                }
                return itemsRender
              })}
            </Grid>
          </Grid>
        )}
        {loading && (
          <div className={styles['skeleton-list']}>
            {Array.from({ length: config?.pageLimit ?? PAGE_LIMIT_ARTICLES_DEFAULT }).map(
              (item, idx) => (
                // The height variation is given by the card that appears in the list in a position determined by config?.cardCtaPosition
                <AchieveSkeleton
                  key={idx}
                  width={'100%'}
                  height={idx + 1 === config?.cardCtaPosition ? 400 : 130}
                />
              )
            )}
          </div>
        )}
        <Grid item xs={12} display={'flex'} flexDirection={'row'} justifyContent={'center'} gap={2}>
          {parseInt(state?.articlesObject?.total) /
            parseInt(config?.pageLimit ?? PAGE_LIMIT_ARTICLES_DEFAULT) >
            1 && (
            <Grid item className={styles['pagination']}>
              <AchieveLink
                href={categoryname === 'search' ? null : `#${state?.titleId}`}
                track={{
                  list_name: 'Pagination Event',
                  click_id: `${
                    mainProductConfig[siteEnv || 'achieve']?.['MainProduct']
                  } | Pagination`,
                  click_position: 'Pagination',
                  click_text: 'Pagination',
                  nav_link_section: 'Pagination',
                  track_event: 'ui_click',
                  event_action: 'ui_click',
                  event_type: 'ui_click',
                }}
              >
                <Pagination
                  color="primary"
                  className={styles['pagination-items']}
                  data-testid="article-grid-pagination"
                  count={Math.ceil(
                    parseInt(state?.articlesObject?.total) / parseInt(config.pageLimit)
                  )}
                  onChange={handleChangePage}
                  page={state?.page ? parseInt(state?.page) : 1}
                />
              </AchieveLink>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Section>
  )
}
export { PaginatedArticlesList }
export default PaginatedArticlesList
